import { areFiltersIdentical } from "../../areFiltersIdentical";
import { FilterOperators } from "../../types";
import {
    EditorFilter,
    EditorFilterMessage,
    EditorFilterMessageTypes,
} from "../types";
import { isAndOrOperator } from "./isAndOrOperator";

// // // //

// Returns a boolean if a Filter.value is a valid date string
function isValidDateString(dateString: any): boolean {
    // Check if the input is a string
    if (typeof dateString !== "string") {
        return false;
    }

    // Attempt to parse the string as a date
    const date = new Date(dateString);

    // Check if the date is valid
    return !isNaN(date.getTime());
}

// // // //

// Returns a boolean if a Filter.operator is valid for a Date filter
function isValidDateOperator(operator: FilterOperators): boolean {
    return [
        FilterOperators.Equals,
        FilterOperators.NotEquals,
        FilterOperators.After,
        FilterOperators.OnOrAfter,
        FilterOperators.Before,
        FilterOperators.OnOrBefore,
    ].includes(operator);
}

// // // //

// Helper function to gather Filter.field values that are duplicated inside a parent Filter
function findDuplicateFieldNames(arr: string[]): string[] {
    const duplicates: string[] = [];
    const uniqueValues: Set<string> = new Set();

    for (const item of arr) {
        if (uniqueValues.has(item)) {
            duplicates.push(item);
        } else {
            uniqueValues.add(item);
        }
    }

    return duplicates;
}

// // // //

// Identifies duplicate filters inside a parent Filter
// If there are multiple filters that have the same `field` property, we'll return those field names
// Only if the two filters that share the same `field` property are *NOT* date fields.
// We don't have a 100% reliable, so we do the following checks:
// - Ensure that EditorFilter.state.value is a valid date string
// - Ensure that EditorFilter.state.operator is a valid operator for Date filters
// NOTE - we don't need to check for *identical* filters in this function, that's handled in Step #7 below.
function findDuplicateFilters(filters: EditorFilter[]): EditorFilter[] {
    const duplicates: EditorFilter[] = [];

    const fields = filters.map((each) => each.state.field) as string[];

    const duplicateFieldNames = findDuplicateFieldNames(fields);

    // Return empty array if none of the filters share the same `field` property
    if (duplicateFieldNames.length === 0) {
        return [];
    }

    // Iterate over each duplicate field name
    for (const fieldName of duplicateFieldNames) {
        // Find filters with same field
        const filtersWithDuplicateFieldName: EditorFilter[] = filters.filter(
            (each) => each.state.field === fieldName,
        );

        // Check if each value in filtersWithDuplicateFieldName is a date filter using the logic outlined about
        const areDuplicatesDateFilters: boolean =
            filtersWithDuplicateFieldName.every((f) => {
                return (
                    isValidDateOperator(f.state.operator as FilterOperators) &&
                    isValidDateString(f.state.value)
                );
            });

        // If the duplicate filters ARE valid date filters -> do nothing.
        // If they are NOT all valid date filters -> add them to the duplicates array returned by this function
        if (!areDuplicatesDateFilters) {
            duplicates.concat(filtersWithDuplicateFieldName);
        }
    }

    return duplicates;
}

// // // //

function findIdenticalFilters(
    filters: EditorFilter[],
): Record<string, EditorFilter[]> {
    const identicalFilters: Record<string, EditorFilter[]> = {};

    for (let i = 0; i < filters.length; i++) {
        const currentFilter = filters[i];

        for (let j = i + 1; j < filters.length; j++) {
            const compareFilter = filters[j];

            if (
                areFiltersIdentical({
                    currentFilter: currentFilter.state,
                    latestFilter: compareFilter.state,
                })
            ) {
                // Define a key for each group of identical filters
                // NOTE - this is necessary beacuse simply grouping by "field" doesn't work
                // if there are 2 pairs of identical filters belonging to the same field
                const { state } = currentFilter;
                const key = `${state.field}-${state.operator}-${state.value}`;

                if (!identicalFilters[key]) {
                    identicalFilters[key] = [currentFilter];
                }
                identicalFilters[key].push(compareFilter);
            }
        }
    }

    return identicalFilters;
}

// // // //

/**
 * validateEditorFilter
 * Recursively validates an EditorFilter instance by adding values to EditorFilter.messages
 */
export function validateEditorFilter(filter: EditorFilter): EditorFilter {
    // Define an empty array for all new EditorFilterMessage data
    const newMessages: EditorFilterMessage[] = filter.messages;

    // // // //
    // // // //

    // Check composite operators first
    if (isAndOrOperator(filter.state.operator)) {
        // // // //
        // Check #1 - Check for "Empty Composite" warning
        if (filter.nested.length === 0) {
            newMessages.push({
                type: EditorFilterMessageTypes.emptyComposite,
                variant: "warning",
            });

            // Set filter.messages and return
            filter.messages = newMessages;
            return filter;
        }

        //
        // // // //

        // Get list of nested composite + basic filters
        const compositeFilters = filter.nested.filter((each) =>
            isAndOrOperator(each.state.operator),
        );
        const basicFilters = filter.nested.filter(
            (each) => !isAndOrOperator(each.state.operator),
        );

        // // // //
        // Check #2 - Check for for "Redundant Congruent Parent Composite" warning
        if (
            compositeFilters.length > 0 &&
            compositeFilters.length === filter.nested.length &&
            compositeFilters.every(
                (each) => each.state.operator === filter.state.operator,
            )
        ) {
            newMessages.push({
                type: EditorFilterMessageTypes.redundantCongruentParentComposite,
                variant: "warning",
            });
        }

        // // // //
        // Check #3 - Check for for "Redundant Parent Composite" warning
        if (
            compositeFilters.length === 1 &&
            compositeFilters.length === filter.nested.length &&
            compositeFilters[0].state.operator !== filter.state.operator &&
            isAndOrOperator(compositeFilters[0].state.operator)
        ) {
            newMessages.push({
                type: EditorFilterMessageTypes.redundantParentComposite,
                variant: "warning",
            });
        }

        // // // //
        // Check #4 - Check for "Redundant Composite" warning
        if (
            compositeFilters.length > 0 &&
            compositeFilters.filter(
                (each) => each.state.operator === filter.state.operator,
            ).length > 0
        ) {
            compositeFilters
                .filter((each) => each.state.operator === filter.state.operator)
                .forEach((each) => {
                    if (
                        each.nested.filter((f) =>
                            isAndOrOperator(f.state.operator),
                        ).length === 0
                    ) {
                        each.messages.push({
                            type: EditorFilterMessageTypes.redundantComposite,
                            variant: "warning",
                        });
                    }
                });
        }

        // // // //
        // Check #5 - Check for "Redundant And Clause" warning

        if (
            compositeFilters.length > 0 &&
            compositeFilters.filter(
                (each) =>
                    each.state.operator === FilterOperators.And &&
                    each.nested.length === 1,
            ).length > 0
        ) {
            compositeFilters
                .filter(
                    (each) =>
                        each.state.operator === FilterOperators.And &&
                        each.nested.length === 1,
                )
                .forEach((each) => {
                    each.messages.push({
                        type: EditorFilterMessageTypes.redundantAndClause,
                        variant: "warning",
                    });
                });
        }

        // // // //
        // Check #5 - Check for "Redundant Or Clause" warning

        if (
            compositeFilters.length > 0 &&
            compositeFilters.filter(
                (each) =>
                    each.state.operator === FilterOperators.Or &&
                    each.nested.length === 1,
            ).length > 0
        ) {
            compositeFilters
                .filter(
                    (each) =>
                        each.state.operator === FilterOperators.Or &&
                        each.nested.length === 1,
                )
                .forEach((each) => {
                    each.messages.push({
                        type: EditorFilterMessageTypes.redundantOrClause,
                        variant: "warning",
                    });
                });
        }

        // // // //
        // Check #6 - Check for "Duplicate Field Inside And Operator" error

        // Get a list of nested basic fields that share the same "state.field" property
        // Queries will not work when duplicate filters are wrapped inside an "AND" composite,
        // since (field == 'A' AND field == 'B') will always return 0 results
        // NOTE that this only applies to the "AND" composite operator
        const duplicateNestedFields = findDuplicateFilters(basicFilters);

        if (
            filter.state.operator === FilterOperators.And &&
            duplicateNestedFields.length > 0
        ) {
            newMessages.push({
                type: EditorFilterMessageTypes.andOperatorContainsDuplicateFields,
                variant: "error",
            });

            duplicateNestedFields.forEach((duplicateField) => {
                basicFilters.forEach((each) => {
                    if (each.state.field === duplicateField.state.field) {
                        each.messages.push({
                            type: EditorFilterMessageTypes.duplicateBasicFilterInsideAndClause,
                            variant: "error",
                        });
                    }
                });
            });
        }

        // // // //
        // Check #7 - Check for "Group Contains Identical Fields" error

        if (isAndOrOperator(filter.state.operator)) {
            // Get a list of nested basic fields that share the same "field" + "operator" + "value" properties
            const identicalNestedFiltersByField =
                findIdenticalFilters(basicFilters);

            // Capture array of state.field values from identicalNestedFiltersByField
            const identicalNestedKeys = Object.keys(
                identicalNestedFiltersByField,
            );

            // Iterate over the array of keys (combination of field+operator+value) of identical filters
            identicalNestedKeys.forEach((key) => {
                // Add groupContainsIdenticalFields message to parent filter
                // NOTE - we intentionally do this one for each set of identical Filters
                newMessages.push({
                    type: EditorFilterMessageTypes.groupContainsIdenticalFields,
                    variant: "warning",
                });

                // Update each identical nested filter to include the "identicaBasicFilterInsideGroup" message
                identicalNestedFiltersByField[key].forEach(
                    (identicalFilter) => {
                        identicalFilter.messages.push({
                            type: EditorFilterMessageTypes.identicaBasicFilterInsideGroup,
                            variant: "warning",
                        });
                    },
                );
            });
        }
    }

    // // // //
    // // // //

    // Check basic filter validation errors
    if (!isAndOrOperator(filter.state.operator)) {
        // Check for "missing field" message
        if (
            filter.state.field === undefined ||
            filter.state.field === null ||
            filter.state.field === ""
        ) {
            newMessages.push({
                type: EditorFilterMessageTypes.missingField,
                variant: "error",
            });
        }

        // Check for "missing operator" message
        if (
            filter.state.operator === undefined ||
            filter.state.operator === null
        ) {
            newMessages.push({
                type: EditorFilterMessageTypes.missingOperator,
                variant: "error",
            });
        }

        // Check for "missing value" message
        if (
            filter.state.value === undefined ||
            filter.state.value === "" ||
            (Array.isArray(filter.state.value) &&
                filter.state.value.length === 0)
        ) {
            newMessages.push({
                type: EditorFilterMessageTypes.missingValue,
                variant: "error",
            });
        }
    }

    // // // //
    // // // //

    // Set filter.messages
    filter.messages = newMessages;

    // Recursively check nested filters
    filter.nested.forEach((nestedFilter) => {
        validateEditorFilter(nestedFilter);
    });

    // Return the filter with validation messages
    return filter;
}
